import { TemperatureUnits } from "@/libs/units/temperature";
import type { WaterDensityPwCalculator } from "../d";
import {
  calculateMolarSaltConcentration,
  calculateSaltConcentration,
  calculatePwAt70Mpa,
  calculateEwo,
  calculateFwo,
  calculatePureWaterDensity,
  calculateDm2,
  calculateDm32,
  calculateDm1,
  calculateDm12,
  calculatePbwAt70Mpa,
  calculateEm,
  calculateFm32,
  calculateFm1,
  calculateFm12,
  Pwd,
  Ewd,
  Fwd,
  Dm2Coeff,
  Dm32Coeff,
  Dm1Coeff,
  Dm12Coeff,
  EmCoeff,
  Fm32Coeff,
  Fm1Coeff,
  Fm12Coeff
} from "./helpers";

export const waterDensityPwCalculator: WaterDensityPwCalculator = params => {
  const {
    salinity_ppm,
    temperatureF,
    inSituPressurePSIA,
    waterSpecificGravity,
    gasMolecularWeight
  } = params;
  // N44: =C3+273.15
  const inSituTemperatureK = TemperatureUnits.Kelvin.toThisUnit(temperatureF);
  const inSituTemperatureC = TemperatureUnits.Celsius.toThisUnit(temperatureF);

  // N46: =C2*0.00689476
  const inSituPressureMPa = inSituPressurePSIA * 0.00689476;

  const salinity = calculateSaltConcentration(salinity_ppm);

  // N48: =(((C7/1000000)/(58.4428))/1000)
  const molarSaltConcentration = calculateMolarSaltConcentration(salinity_ppm);

  // N50: =N48*(22.989769/58.4428)
  const referencePressure = molarSaltConcentration * (22.989769 / 58.4428);

  // N52: =(R52*(N44/100)^2+R53*(N44/100)+R54)/(R55*(N44/100)^2+R56*(N44/100)+1)
  const pw70Mpa = calculatePwAt70Mpa(
    Pwd[0],
    Pwd[1],
    Pwd[2],
    Pwd[3],
    Pwd[4],
    inSituTemperatureC
  );

  // N54: =(S52*(N44/100)^2+S53*(N44/100)+S54)/(S55*(N44/100)^2+S56*(N44/100)+1)
  const Ewo = calculateEwo(
    Ewd[0],
    Ewd[1],
    Ewd[2],
    Ewd[3],
    Ewd[4],
    inSituTemperatureC
  );

  // N56: =(T52*(N44/100)^2+T53*(N44/100)+T54)/(T55*(N44/100)^2+T56*(N44/100)+1)
  const Fwo = calculateFwo(
    Fwd[0],
    Fwd[1],
    Fwd[2],
    Fwd[3],
    Fwd[4],
    inSituTemperatureC
  );

  // N58: =(1/N54)*LN(N54*(N50/70)+N56)
  const Iw = (1 / Ewo) * Math.log(Ewo * (referencePressure / 70) + Fwo);

  // N60: =(1/N54)*LN(N54*(N45/70)+N56)
  const IwAtP = (1 / Ewo) * Math.log(Ewo * (inSituPressureMPa / 70) + Fwo);

  // N62: =N52*EXP(N60-N58)
  const pureDensity = calculatePureWaterDensity(pw70Mpa, IwAtP, Iw);

  // N64: =(R66*(N44/100)^2+R67*(N44/100)+R68)/(R69*(N44/100)^2+R70*(N44/100)+1)
  const Dm2 = calculateDm2(
    Dm2Coeff[0],
    Dm2Coeff[1],
    Dm2Coeff[2],
    Dm2Coeff[3],
    Dm2Coeff[4],
    inSituTemperatureC
  );
  // N66: =(S66*(N44/100)^2+S67*(N44/100)+S68)/(S69*(N44/100)^2+S70*(N44/100)+1)
  const Dm32 = calculateDm32(
    Dm32Coeff[0],
    Dm32Coeff[1],
    Dm32Coeff[2],
    Dm32Coeff[3],
    Dm32Coeff[4],
    inSituTemperatureC
  );

  // N68: =(T66*(N44/100)^2+T67*(N44/100)+T68)/(T69*(N44/100)^2+T70*(N44/100)+1)
  const Dm1 = calculateDm1(
    Dm1Coeff[0],
    Dm1Coeff[1],
    Dm1Coeff[2],
    Dm1Coeff[3],
    Dm1Coeff[4],
    inSituTemperatureC
  );

  // N70: =(U66*(N44/100)^2+U67*(N44/100)+U68)/(U69*(N44/100)^2+U70*(N44/100)+1)
  const Dm12 = calculateDm12(
    Dm12Coeff[0],
    Dm12Coeff[1],
    Dm12Coeff[2],
    Dm12Coeff[3],
    Dm12Coeff[4],
    inSituTemperatureC
  );

  // N74: =N52+N66*N48^2+N68*N48^(3/2)+N70*N48+N72*N48^(1/2)
  const pbw70MPa = calculatePbwAt70Mpa(
    pw70Mpa,
    Dm2,
    Dm32,
    Dm1,
    Dm12,
    molarSaltConcentration
  );

  // N76: =(R77*(N44/100)^2+R78*(N44/100)+R79)/(R80*(N44/100)^2+R81*(N44/100)+1)
  const Em = calculateEm(
    EmCoeff[0],
    EmCoeff[1],
    EmCoeff[2],
    EmCoeff[3],
    EmCoeff[4],
    inSituTemperatureC
  );

  // N78: =(S77*(N44/100)^2+S78*(N44/100)+S79)/(S80*(N44/100)^2+S81*(N44/100)+1)
  const Fm32 = calculateFm32(
    Fm32Coeff[0],
    Fm32Coeff[1],
    Fm32Coeff[2],
    Fm32Coeff[3],
    Fm32Coeff[4],
    inSituTemperatureC
  );

  // N80: =(T77*(N44/100)^2+T78*(N44/100)+T79)/(T80*(N44/100)^2+T81*(N44/100)+1)
  const Fm1 = calculateFm1(
    Fm1Coeff[0],
    Fm1Coeff[1],
    Fm1Coeff[2],
    Fm1Coeff[3],
    Fm1Coeff[4],
    inSituTemperatureC
  );

  // N82: =(U77*(N44/100)^2+U78*(N44/100)+U79)/(U80*(N44/100)^2+U81*(N44/100)+1)
  const Fm12 = calculateFm12(
    Fm12Coeff[0],
    Fm12Coeff[1],
    Fm12Coeff[2],
    Fm12Coeff[3],
    Fm12Coeff[4],
    inSituTemperatureC
  );

  // N84: =N54+N74*N48
  const Eb = Ewo + Em * molarSaltConcentration;

  // N86: =N56+N76*N48^(3/2)+N78*N48+N80*N48^(1/2)
  const Fb =
    Fwo +
    Fm32 * Math.pow(molarSaltConcentration, 3 / 2) +
    Fm1 * molarSaltConcentration +
    Fm12 * Math.sqrt(molarSaltConcentration);

  // N90: =(1/N84)*LN(N84+N86)
  const Ib = (1 / Eb) * Math.log(Eb + Fb);

  // N92: =(1/N84)*LN(N84*(N46/70)+N86)
  const IbAtP = (1 / Eb) * Math.log(Eb * (inSituPressureMPa / 70) + Fb);

  // N94: =N74*EXP(N92-N90)
  const pbwAtT = pbw70MPa * Math.exp(IbAtP - Ib);

  // N96: =1-(N44/647.096)
  const tempConstant = 1 - inSituTemperatureK / 647.096;

  const vaporPressureCoef = [
    -7.85951783, 1.84408259, -11.7866497, 22.6807411, -15.9618719, 1.80122502
  ];
  // N98: =(EXP((647.096/N44)*(R90*N96+R91*N96^1.5+R92*N96^3+R93*N96^3.5+R94*N96^4+R95*N96^7.5)))*22.064
  const pureWaterVaporPressurePv =
    Math.exp(
      (647.096 / inSituTemperatureK) *
        (vaporPressureCoef[0] * tempConstant +
          vaporPressureCoef[1] * Math.pow(tempConstant, 1.5) +
          vaporPressureCoef[2] * Math.pow(tempConstant, 3) +
          vaporPressureCoef[3] * Math.pow(tempConstant, 3.5) +
          vaporPressureCoef[4] * Math.pow(tempConstant, 4) +
          vaporPressureCoef[0] * Math.pow(tempConstant, 7.5))
    ) * 22.064;

  const A = [0, -0.004462, -0.06763, 0, 0];
  const B = [-0.03602, 0.18917, 0.97242, 0, 0];
  const C = [0.6855, -3.1992, -3.7968, 0.07711, 0.2229];

  // N100: =(R101*(N45/100)^2+R102*(N45/100)+R103)/(R104*(N45/100)^2+R105*(N45/100)+1)
  const At =
    (A[0] * Math.pow(inSituTemperatureC / 100, 2) +
      A[1] * (inSituTemperatureC / 100) +
      A[2]) /
    (A[3] * Math.pow(inSituTemperatureC / 100, 2) +
      A[4] * (inSituTemperatureC / 100) +
      1);

  // N102: =(S101*(N45/100)^2+S102*(N45/100)+S103)/(S104*(N45/100)^2+S105*(N45/100)+1)
  const Bt =
    (B[0] * Math.pow(inSituTemperatureC / 100, 2) +
      B[1] * (inSituTemperatureC / 100) +
      B[2]) /
    (B[3] * Math.pow(inSituTemperatureC / 100, 2) +
      B[4] * (inSituTemperatureC / 100) +
      1);

  // N104: =(T101*(N45/100)^2+T102*(N45/100)+T103)/(T104*(N45/100)^2+T105*(N45/100)+1)
  const Ct =
    (C[0] * Math.pow(inSituTemperatureC / 100, 2) +
      C[1] * (inSituTemperatureC / 100) +
      C[2]) /
    (C[3] * Math.pow(inSituTemperatureC / 100, 2) +
      C[4] * (inSituTemperatureC / 100) +
      1);

  // N106: =EXP(N100*(LN(N46-N98))^2+N102*LN(N46-N98)+N104)
  let methaneWaterSolutibility = Math.exp(
    At * Math.pow(Math.log(inSituPressureMPa - pureWaterVaporPressurePv), 2) +
      Bt * Math.log(inSituPressureMPa - pureWaterVaporPressurePv) +
      Ct
  );
  if (!Number.isFinite(methaneWaterSolutibility)) {
    methaneWaterSolutibility = 0;
  }
  // N108: =-0.00389
  const zeta = -0.00389;

  // N110: =-0.80898+0.0010827*N44+(183.85/N44)+0.0003924*N46+(-0.00389)*N46^2
  const lambda =
    -0.80898 +
    0.0010827 * inSituTemperatureK +
    183.85 / inSituTemperatureK +
    0.0003924 * inSituPressureMPa +
    zeta * Math.pow(inSituPressureMPa, 2);

  // N112: =N106*EXP(-2*N110*N50-N108*N48^2)
  const cmBrine =
    methaneWaterSolutibility *
    Math.exp(
      -2 * lambda * referencePressure -
        zeta * Math.pow(molarSaltConcentration, 2)
    );

  // N114: =0.07698589+(-0.000050253331*N44)+(-30.092013/N44)+(4846.8502)/(N44^2)
  const viscosityDerivative =
    0.07698589 -
    0.000050253331 * inSituTemperatureK -
    30.092013 / inSituTemperatureK +
    4846.8502 / Math.pow(inSituTemperatureK, 2);

  // N116: =0.0003924+2*-0.000197*N46
  const lambdaDerivative = 0.0003924 + 2 * -0.000197 * inSituPressureMPa;

  // N118: =8.314467*N44
  const RT = 8.314467 * inSituTemperatureK;

  // N120: =N118*(N114+2*N48*N116)
  const partialMolarVolumeCH4 =
    RT * (viscosityDerivative + 2 * molarSaltConcentration * lambdaDerivative);

  // N122: =1/N94
  const specificVolumeFreeBrine = 1 / pbwAtT;

  // N124: =(1000+N48*58.4428+N112*C13)/((1000+N48*58.4428)*N122+N112*N120)
  const brineWaterDensityCH4 =
    (1000 + molarSaltConcentration * 58.4428 + cmBrine * gasMolecularWeight) /
    ((1000 + molarSaltConcentration * 58.4428) * specificVolumeFreeBrine +
      cmBrine * partialMolarVolumeCH4);

  // N126: =1/N124
  const specificVolumeBrine = 1 / brineWaterDensityCH4;

  // N128: =N124*62.4280*C10
  const waterDensityLBMFT3 =
    brineWaterDensityCH4 * 62.428 * waterSpecificGravity;

  return {
    waterDensityLBMFT3,
    partialMolarVolumeCH4,
    RT,
    lambdaDerivative,
    viscosityDerivative,
    cmBrine,
    lambda,
    salinty: salinity,
    specificVolumeFreeBrine,
    brineWaterDensityCH4,
    specificVolumeBrine,
    pureDensity
  };
};
